import { useEffect, useState } from 'react';

import {
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import { Address } from 'types/graphql';

import { useMutation } from '@redwoodjs/web';

import SingleAddressBlock from 'src/components/common/form/SingleAddressBlock';
import { CustomCounter } from 'src/components/CustomCounter';
import { CustomInput } from 'src/components/CustomInput';
import { CurrencyCounter } from 'src/components/FormikComponents/CurrencyCounter';
import { PercentageCounter } from 'src/components/FormikComponents/PercentageCounter';
import Loader from 'src/components/Loader/Loader';
import MainCard from 'src/components/MainCard/MainCard';
import { AddressListModal } from 'src/components/Modals';
import { showErrorToast } from 'src/components/Toasts/toast';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { ADDRESS_LOOK_UP } from 'src/graphql/mutations/addressLookUp.mutation';
import getPostalLabel from 'src/lib/postalLabel';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';
import { getCurrencySymbol } from 'src/utils/utils';

import { AddressList, OpenModalState } from '../AddressInformation';

const GoodsAndServicesForm = ({ values, errors, setFieldValue, setValues }) => {
    const { user } = useAuthContext();
    const { merchantApplication } = useAddMerchantContext();
    const isEditingDisabled = isApplicationEditable(merchantApplication);
    const postalLabel = getPostalLabel(merchantApplication?.partner?.countryDetails?.alpha2Code);
    const currency = getCurrencySymbol(user?.business?.currency || 'EUR');
    // Modal state management
    const [openAddressModal, setOpenAddressModal] = useState<OpenModalState>({
        open: false,
    });

    // Address data
    const [addresses, setAddresses] = useState<AddressList[] | null>();

    // Loqate API integration
    const [addressLookUp, { loading: searchingForAddress }] = useMutation(ADDRESS_LOOK_UP, {
        onError: () => {
            showErrorToast('Error Searching for Address');
        },
        onCompleted: ({ addressLookUp }) => {
            setAddresses(addressLookUp);
            setOpenAddressModal((prev) => {
                return { ...prev, open: true };
            });
        },
    });

    // Submit handler for the search button
    const findAddress = async (value) => {
        await addressLookUp({ variables: { address: value } });
        setOpenAddressModal({ open: true });
    };

    const onRetrievalOfCountry = (address: Address) => {
        setValues((prev) => {
            return {
                ...prev,
                tradingAddress_addressLine1: address.line1,
                tradingAddress_addressLine2: address.line2,
                tradingAddress_townOrCity: address.city,
                tradingAddress_county: address.province,
                tradingAddress_country: address.countryName,
                stockEircode: address.postalCode,
            };
        });
    };

    useEffect(() => {
        if (values.stocksHeldAnyAddress === false) {
            setValues((prev) => ({
                ...prev,
                tradingAddress_addressLine1: '',
                tradingAddress_addressLine2: '',
                tradingAddress_townOrCity: '',
                tradingAddress_county: '',
                tradingAddress_country: '',
                stockEircode: '',
            }));
        }
    }, [values.stocksHeldAnyAddress, setValues]);

    return (
        <MainCard>
            {searchingForAddress && <Loader />}
            <Typography fontSize="24px" color="#575757" marginBottom="8px">
                Goods and Services
            </Typography>
            <Divider
                sx={{
                    borderColor: 'warning.main',
                    marginBottom: '34px',
                }}
            />

            <Grid container spacing={2} rowGap={2}>
                <Grid item xs={12} lg={6}>
                    <Stack direction="column" spacing={2}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{
                                flexWrap: 'wrap',
                                rowGap: '8px',
                            }}
                        >
                            <FormLabel
                                id="goodsServicesOwned"
                                sx={{
                                    color: '#575757',
                                    flex: 1,
                                    minWidth: { xs: '100%', sm: '200px' },
                                }}
                            >
                                Are the goods or services to be sold or supplied, owned or to be
                                provided by a third party?
                            </FormLabel>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby="goodsServicesOwned"
                                    value={values.goodsServicesOwned}
                                    name="goodsServicesOwned"
                                    row
                                    onChange={(event) => {
                                        setFieldValue(
                                            'goodsServicesOwned',
                                            JSON.parse(event.target.value)
                                        );
                                    }}
                                >
                                    <FormControlLabel
                                        value
                                        control={<Radio />}
                                        label="Yes"
                                        disabled={isEditingDisabled}
                                    />
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio />}
                                        label="No"
                                        disabled={isEditingDisabled}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{
                                flexWrap: 'wrap',
                                rowGap: '8px',
                            }}
                        >
                            <FormLabel
                                id="acceptDepositPayment"
                                sx={{
                                    color: '#575757',
                                    flex: 1,
                                    minWidth: { xs: '100%', sm: '200px' },
                                }}
                            >
                                Do you accept deposits prior to the supply of goods or services?
                            </FormLabel>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby="acceptDepositPayment"
                                    value={values.acceptDepositPayment}
                                    name="acceptDepositPayment"
                                    row
                                    onChange={(event) => {
                                        setFieldValue(
                                            'acceptDepositPayment',
                                            JSON.parse(event.target.value)
                                        );
                                    }}
                                >
                                    <FormControlLabel
                                        value
                                        control={<Radio />}
                                        label="Yes"
                                        disabled={isEditingDisabled}
                                    />
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio />}
                                        label="No"
                                        disabled={isEditingDisabled}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                        {values.acceptDepositPayment && (
                            <>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel
                                        id="percentageDepositTaken"
                                        sx={{ color: '#575757' }}
                                    >
                                        The percentage (%) of goods or services where a deposit is
                                        taken?
                                    </FormLabel>
                                    <PercentageCounter
                                        name="percentageDepositTaken"
                                        placeholder="%"
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel id="sizeOfDepositTaken" sx={{ color: '#575757' }}>
                                        The size of the deposit paid in advance as % of total
                                        transaction value
                                    </FormLabel>
                                    <PercentageCounter
                                        name="sizeOfDepositTaken"
                                        placeholder="%"
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel
                                        id="averageTimeFullPaymentTaken"
                                        sx={{ color: '#575757' }}
                                    >
                                        The average time, in advance of full payment, that deposits
                                        are taken?
                                    </FormLabel>
                                    <CustomCounter
                                        name="averageTimeFullPaymentTaken"
                                        label=""
                                        onChange={(count) => {
                                            return setFieldValue(
                                                'averageTimeFullPaymentTaken',
                                                count
                                            );
                                        }}
                                        value={values.averageTimeFullPaymentTaken}
                                        error={errors.averageTimeFullPaymentTaken}
                                        placeholder="Days"
                                        boxStyling={{
                                            width: '200px',
                                            maxWidth: '200px',
                                        }}
                                        min={1}
                                        max={365}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel
                                        id="averageTimeAdvancePaymentTaken"
                                        sx={{ color: '#575757' }}
                                    >
                                        The average time, in advance of delivery of goods/services
                                        of full payment, that balance payment is taken?
                                    </FormLabel>
                                    <CustomCounter
                                        name="averageTimeAdvancePaymentTaken"
                                        label=""
                                        onChange={(count) => {
                                            return setFieldValue(
                                                'averageTimeAdvancePaymentTaken',
                                                count
                                            );
                                        }}
                                        value={values.averageTimeAdvancePaymentTaken}
                                        error={errors.averageTimeAdvancePaymentTaken}
                                        placeholder="Days"
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        min={1}
                                        max={365}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                            </>
                        )}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{
                                flexWrap: 'wrap',
                                rowGap: '8px',
                            }}
                        >
                            <FormLabel
                                id="stocksHeldAnyAddress"
                                sx={{
                                    color: '#575757',
                                    flex: 1,
                                    minWidth: { xs: '100%', sm: '200px' },
                                }}
                            >
                                Are stocks held at any address other than the trading address?
                            </FormLabel>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby="stocksHeldAnyAddress"
                                    value={values.stocksHeldAnyAddress}
                                    name="stocksHeldAnyAddress"
                                    row
                                    onChange={(event) => {
                                        setFieldValue(
                                            'stocksHeldAnyAddress',
                                            JSON.parse(event.target.value)
                                        );
                                    }}
                                >
                                    <FormControlLabel
                                        value
                                        control={<Radio />}
                                        label="Yes"
                                        disabled={isEditingDisabled}
                                    />
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio />}
                                        label="No"
                                        disabled={isEditingDisabled}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                        {values.stocksHeldAnyAddress && (
                            <SingleAddressBlock
                                label="Address Information"
                                formKeys={{
                                    addressLine1: 'tradingAddress_addressLine1',
                                    addressLine2: 'tradingAddress_addressLine2',
                                    townOrCity: 'tradingAddress_townOrCity',
                                    county: 'tradingAddress_county',
                                    country: 'tradingAddress_country',
                                    eircode: 'stockEircode',
                                }}
                                setFieldValue={setFieldValue}
                            />
                        )}
                    </Stack>
                </Grid>

                <Grid item xs={12} lg={6}>
                    <Stack direction="column" spacing={2}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{
                                flexWrap: 'wrap',
                                rowGap: '8px',
                            }}
                        >
                            <FormLabel
                                id="acceptFullPayment"
                                sx={{
                                    color: '#575757',
                                    flex: 1,
                                    minWidth: { xs: '100%', sm: '200px' },
                                }}
                            >
                                Do you accept full payment prior to the supply of goods or services?
                            </FormLabel>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby="acceptFullPayment"
                                    value={values.acceptFullPayment}
                                    name="acceptFullPayment"
                                    row
                                    onChange={(event) => {
                                        setFieldValue(
                                            'acceptFullPayment',
                                            JSON.parse(event.target.value)
                                        );
                                    }}
                                >
                                    <FormControlLabel
                                        value
                                        control={<Radio />}
                                        label="Yes"
                                        disabled={isEditingDisabled}
                                    />
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio />}
                                        label="No"
                                        disabled={isEditingDisabled}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                        {values.acceptFullPayment && (
                            <>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel
                                        id="percentagePaymentPriorToDelivery"
                                        sx={{ color: '#575757' }}
                                    >
                                        The percentage of goods or services where payment is taken
                                        prior to delivery?
                                    </FormLabel>
                                    <PercentageCounter
                                        name="percentagePaymentPriorToDelivery"
                                        placeholder="%"
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel
                                        id="averageTimeFullPaymentDeliveryTaken"
                                        sx={{ color: '#575757' }}
                                    >
                                        The average time, in advance of delivery of goods/services,
                                        that full payment is taken?
                                    </FormLabel>
                                    <CustomCounter
                                        name="averageTimeFullPaymentDeliveryTaken"
                                        label=""
                                        onChange={(count) => {
                                            return setFieldValue(
                                                'averageTimeFullPaymentDeliveryTaken',
                                                count
                                            );
                                        }}
                                        value={values.averageTimeFullPaymentDeliveryTaken}
                                        error={errors.averageTimeFullPaymentDeliveryTaken}
                                        placeholder="Days"
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        min={1}
                                        max={365}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                            </>
                        )}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{
                                flexWrap: 'wrap',
                                rowGap: '8px',
                            }}
                        >
                            <FormLabel
                                id="levyChargeForGuarantees"
                                sx={{
                                    color: '#575757',
                                    flex: 1,
                                    minWidth: { xs: '100%', sm: '200px' },
                                }}
                            >
                                Do you levy a charge for any guarantees or extended warranties?
                            </FormLabel>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby="levyChargeForGuarantees"
                                    value={values.levyChargeForGuarantees}
                                    name="levyChargeForGuarantees"
                                    row
                                    onChange={(event) => {
                                        setFieldValue(
                                            'levyChargeForGuarantees',
                                            JSON.parse(event.target.value)
                                        );
                                    }}
                                >
                                    <FormControlLabel
                                        value
                                        control={<Radio />}
                                        label="Yes"
                                        disabled={isEditingDisabled}
                                    />
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio />}
                                        label="No"
                                        disabled={isEditingDisabled}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                        {values.levyChargeForGuarantees && (
                            <>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel
                                        id="percentageOfCardTurnover"
                                        sx={{ color: '#575757' }}
                                    >
                                        The separate percentage of card turnover on guarantees/
                                        warranties?
                                    </FormLabel>
                                    <PercentageCounter
                                        name="percentageOfCardTurnover"
                                        label=""
                                        placeholder="%"
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel
                                        id="averageLengthOfGuarantees"
                                        sx={{ color: '#575757' }}
                                    >
                                        The average length of guarantees/warranties sold?
                                    </FormLabel>
                                    <CustomCounter
                                        name="averageLengthOfGuarantees"
                                        label=""
                                        onChange={(count) => {
                                            return setFieldValue(
                                                'averageLengthOfGuarantees',
                                                count
                                            );
                                        }}
                                        value={values.averageLengthOfGuarantees}
                                        error={errors.averageLengthOfGuarantees}
                                        placeholder="Months"
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel
                                        id="percentageOfGoodsReturned"
                                        sx={{ color: '#575757' }}
                                    >
                                        Percentage of goods returned whilst under guarantee/warranty
                                    </FormLabel>
                                    <PercentageCounter
                                        name="percentageOfGoodsReturned"
                                        label=""
                                        placeholder="%"
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                            </>
                        )}

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{
                                flexWrap: 'wrap',
                                rowGap: '8px',
                            }}
                        >
                            <FormLabel
                                id="guaranteesWarrantiesThirdParty"
                                sx={{
                                    color: '#575757',
                                    flex: 1,
                                    minWidth: { xs: '100%', sm: '200px' },
                                }}
                            >
                                Are guarantees/warranties provided by a third party?
                            </FormLabel>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby="guaranteesWarrantiesThirdParty"
                                    value={values.guaranteesWarrantiesThirdParty}
                                    name="guaranteesWarrantiesThirdParty"
                                    row
                                    onChange={(event) => {
                                        setFieldValue(
                                            'guaranteesWarrantiesThirdParty',
                                            JSON.parse(event.target.value)
                                        );
                                    }}
                                >
                                    <FormControlLabel
                                        value
                                        control={<Radio />}
                                        label="Yes"
                                        disabled={isEditingDisabled}
                                    />
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio />}
                                        label="No"
                                        disabled={isEditingDisabled}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                        {values.guaranteesWarrantiesThirdParty && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={2}
                            >
                                <FormLabel id="thirdPartyProviderName" sx={{ color: '#575757' }}>
                                    <Typography>
                                        Guarantees/warranties third party provider name
                                    </Typography>
                                </FormLabel>
                                <FormControl
                                    sx={{
                                        alignItems: 'flex-end',
                                        marginLeft: '24px',
                                    }}
                                >
                                    <CustomInput
                                        name="thirdPartyProviderName"
                                        label=""
                                        onChange={(event) => {
                                            return setFieldValue(
                                                'thirdPartyProviderName',
                                                event.target.value
                                            );
                                        }}
                                        value={values.thirdPartyProviderName}
                                        error={errors.thirdPartyProviderName}
                                        placeholder="Enter Third Party Name"
                                        boxStyling={{ minWidth: '200px', width: '100%' }}
                                        disabled={isEditingDisabled}
                                    />
                                </FormControl>
                            </Stack>
                        )}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{
                                flexWrap: 'wrap',
                                rowGap: '8px',
                            }}
                        >
                            <FormLabel
                                id="levyChargeForMembership"
                                sx={{
                                    color: '#575757',
                                    flex: 1,
                                    minWidth: { xs: '100%', sm: '200px' },
                                }}
                            >
                                Do you levy a charge for memberships, subscriptions or insurance
                                premiums?
                            </FormLabel>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby="levyChargeForMembership"
                                    value={values.levyChargeForMembership}
                                    name="levyChargeForMembership"
                                    row
                                    onChange={(event) => {
                                        setFieldValue(
                                            'levyChargeForMembership',
                                            JSON.parse(event.target.value)
                                        );
                                    }}
                                >
                                    <FormControlLabel
                                        value
                                        control={<Radio />}
                                        label="Yes"
                                        disabled={isEditingDisabled}
                                    />
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio />}
                                        label="No"
                                        disabled={isEditingDisabled}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                        {values.levyChargeForMembership && (
                            <>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel
                                        id="percentageOfCardTurnoverForMembership"
                                        sx={{ color: '#575757' }}
                                    >
                                        The percentage of card turnover from memberships,
                                        subscriptions or insurance premiums
                                    </FormLabel>
                                    <PercentageCounter
                                        name="percentageOfCardTurnoverForMembership"
                                        label=""
                                        placeholder="%"
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel
                                        id="averageLengthOfMembership"
                                        sx={{ color: '#575757' }}
                                    >
                                        The average length of memberships, subscriptions or
                                        insurance premiums
                                    </FormLabel>
                                    <CustomCounter
                                        name="averageLengthOfMembership"
                                        label=""
                                        onChange={(count) => {
                                            return setFieldValue(
                                                'averageLengthOfMembership',
                                                count
                                            );
                                        }}
                                        value={values.averageLengthOfMembership}
                                        error={errors.averageLengthOfMembership}
                                        placeholder="Months"
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <FormLabel id="costOfMembership" sx={{ color: '#575757' }}>
                                        Cost of Membership
                                    </FormLabel>
                                    <CurrencyCounter
                                        name="costOfMembership"
                                        label=""
                                        onChange={(count) => {
                                            return setFieldValue('costOfMembership', count);
                                        }}
                                        value={values.costOfMembership}
                                        error={errors.costOfMembership}
                                        placeholder={currency}
                                        currency={currency}
                                        boxStyling={{ width: '200px', minWidth: '200px' }}
                                        disabled={isEditingDisabled}
                                    />
                                </Stack>
                            </>
                        )}
                    </Stack>
                </Grid>
            </Grid>

            <AddressListModal
                isOpen={openAddressModal.open}
                onClose={() => {
                    return setOpenAddressModal((prev) => {
                        return { ...prev, open: false };
                    });
                }}
                addressList={addresses}
                onSelectedAddress={onRetrievalOfCountry}
            />
        </MainCard>
    );
};
export default GoodsAndServicesForm;
